/** Week view — the primary scheduling grid: rows = employees, columns = the 7
 *  days (Mon→Sun). Click an empty cell to add a shift for that person/day; click
 *  a shift chip to edit it. The employee column shows their weekly hours total. */
import { Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../components/ui/cn.js';
import { EmptyState } from '../../../components/ui/Feedback.js';
import { Chip } from '../../../components/ui/StatusPill.js';
import { type ShiftRow, type StaffRow } from '../hooks/useStaff.js';

import {
  formatHours,
  roleTone,
  sameDay,
  shiftHours,
  toTimeInput,
  weekDays,
} from './scheduleUtils.js';

const COLS = '180px repeat(7, minmax(116px, 1fr))';

export function WeekView({
  anchor,
  staff,
  shifts,
  onAdd,
  onEdit,
}: {
  anchor: Date;
  staff: StaffRow[];
  shifts: ShiftRow[];
  onAdd: (staffId: string, date: Date) => void;
  onEdit: (shift: ShiftRow) => void;
}): ReactNode {
  const { t } = useTranslation();
  const days = weekDays(anchor);
  const today = new Date();

  if (staff.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface p-8">
        <EmptyState icon={Users} title={t('staff.schedule.no_staff')} />
      </div>
    );
  }

  function shiftsFor(staffId: string, day: Date): ShiftRow[] {
    return shifts
      .filter(
        (sh) =>
          sh.staff_id === staffId &&
          sh.scheduled_start != null &&
          sameDay(new Date(sh.scheduled_start), day),
      )
      .sort((a, b) => (a.scheduled_start! < b.scheduled_start! ? -1 : 1));
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      {/* Header */}
      <div
        className="grid min-w-[920px] border-b border-border bg-surface-2 text-xs font-semibold uppercase tracking-wide text-muted"
        style={{ gridTemplateColumns: COLS }}
      >
        <div className="px-3 py-2.5">{t('staff.title')}</div>
        {days.map((d) => (
          <div
            key={d.toISOString()}
            className={cn(
              'border-l border-border px-2 py-2.5 text-center',
              sameDay(d, today) && 'text-primary',
            )}
          >
            <div>{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
            <div className="text-sm font-bold normal-case">{d.getDate()}</div>
          </div>
        ))}
      </div>

      {/* Rows */}
      {staff.map((s) => {
        const weekTotal = days.reduce(
          (sum, d) =>
            sum +
            shiftsFor(s.id, d).reduce(
              (h, sh) => h + shiftHours(sh.scheduled_start, sh.scheduled_end),
              0,
            ),
          0,
        );
        return (
          <div
            key={s.id}
            className="grid min-w-[920px] border-b border-border last:border-b-0"
            style={{ gridTemplateColumns: COLS }}
          >
            <div className="px-3 py-2">
              <div className="truncate font-medium text-fg">
                {s.first_name} {s.last_name}
              </div>
              <div className="text-xs text-muted">
                {t('staff.roles.' + s.app_role)} · {formatHours(weekTotal)}{' '}
                {t('staff.schedule.hours_short')}
              </div>
            </div>
            {days.map((d) => {
              const cell = shiftsFor(s.id, d);
              return (
                <div
                  key={d.toISOString()}
                  role="button"
                  tabIndex={0}
                  onClick={() => onAdd(s.id, d)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onAdd(s.id, d);
                  }}
                  className={cn(
                    'group min-h-[60px] cursor-pointer border-l border-border px-1.5 py-1.5 transition-colors hover:bg-surface-2',
                    sameDay(d, today) && 'bg-primary/5',
                  )}
                >
                  <div className="grid gap-1">
                    {cell.map((sh) => (
                      <button
                        key={sh.id}
                        data-testid={`shift-${sh.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(sh);
                        }}
                        className="block w-full"
                      >
                        <Chip
                          tone={roleTone(s.app_role)}
                          className="w-full justify-start font-mono"
                        >
                          {toTimeInput(sh.scheduled_start!)}–{toTimeInput(sh.scheduled_end!)}
                        </Chip>
                      </button>
                    ))}
                    {cell.length === 0 ? (
                      <span className="text-xs text-faint opacity-0 transition-opacity group-hover:opacity-100">
                        + {t('staff.schedule.add_shift')}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
